import { CheckCircleOutlined } from '@ant-design/icons';
import { Flex, Select, Table, Typography } from 'antd';
import { PropsWithChildren, useEffect, useState } from 'react';

import {
    FacilityDto,
    FacilityNurseBlockReason,
    FacilityNurseDto,
    NurseProfileDto,
} from '@clh/api-client';

import { ActionButton } from '../../action-button';
import { useApiClient } from '../../hooks/use-api-client';
import { FetchRecordProvider } from '../../hooks/use-fetch-record';
import { useSelection } from '../../hooks/use-selection';
import ProfileAutoComplete from '../../profiles/profile-autocomplete';

const Title = ({ children }: PropsWithChildren) => (
    <Typography.Title level={4}>{children}</Typography.Title>
);

const BLOCK_REASON_LABELS: Record<FacilityNurseBlockReason, string> = {
    [FacilityNurseBlockReason.CurrentEmployee]: 'Current employee',
    [FacilityNurseBlockReason.NotEligibleForRehire]: 'Not eligible for rehire',
    [FacilityNurseBlockReason.Performance]: 'Performance',
    [FacilityNurseBlockReason.Compliance]: 'Compliance',
    [FacilityNurseBlockReason.Attendance]: 'Attendance',
};

const blockReasonOptions = Object.entries(BLOCK_REASON_LABELS).map(
    ([value, label]) => ({ value, label })
);

export default function NursesTab() {
    const api = useApiClient();
    const { selection } = useSelection<FacilityDto>();
    const [nurses, setNurses] = useState<FacilityNurseDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState();

    const [nurseToVerify, setNurseToVerify] = useState<NurseProfileDto | null>(
        null
    );
    const [verifyKey, setVerifyKey] = useState(0);

    const [nurseToBlock, setNurseToBlock] = useState<NurseProfileDto | null>(
        null
    );
    const [blockReason, setBlockReason] = useState<
        FacilityNurseBlockReason | undefined
    >(undefined);
    const [blockKey, setBlockKey] = useState(0);

    const fetchNurses = async () => {
        if (!api) {
            return;
        }

        try {
            setIsLoading(true);
            const result = await api.facilityControllerListNurses({
                facilityId: selection.id,
            });
            setNurses(result);
        } catch (e) {
            setError(e as any);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void fetchNurses();
    }, [selection.id]);

    return (
        <FetchRecordProvider
            record={nurses}
            fetchRecord={fetchNurses}
            loading={isLoading}
            error={error}
        >
            <div>
                <Title>Nurses</Title>
                <Flex gap="small" style={{ marginBottom: 12 }} align="center">
                    <ProfileAutoComplete
                        key={`verify-${verifyKey}`}
                        autoFocus={false}
                        placeholder="Search nurse to add"
                        role="Nurse"
                        onSelect={(profile) => setNurseToVerify(profile)}
                    />
                    <ActionButton
                        size="small"
                        type="primary"
                        disabled={!nurseToVerify}
                        successMessage="Nurse verified"
                        action={async () => {
                            if (!api || !nurseToVerify) {
                                return;
                            }
                            await api.facilityControllerVerifyNurse({
                                profileId: nurseToVerify.id,
                                facilityId: selection.id,
                            });
                            setNurseToVerify(null);
                            setVerifyKey((k) => k + 1);
                        }}
                    >
                        Add nurse
                    </ActionButton>
                </Flex>
                <Table
                    rowKey="id"
                    bordered
                    size="small"
                    pagination={false}
                    dataSource={nurses.filter((nurse) => !nurse.blockedAt)}
                    columns={[
                        {
                            title: 'First Name',
                            dataIndex: 'firstName',
                        },
                        {
                            title: 'Last Name',
                            dataIndex: 'lastName',
                        },
                        {
                            title: 'Verified',
                            render: (_, row) => (
                                <>
                                    {row.verifiedFacilities.includes(
                                        selection.id
                                    ) && <CheckCircleOutlined />}
                                </>
                            ),
                        },
                        {
                            title: 'Actions',
                            render: (_, row) => (
                                <Flex gap="small">
                                    {row.verifiedFacilities.includes(
                                        selection.id
                                    ) ? (
                                        <ActionButton
                                            size="small"
                                            successMessage="Nurse unverified"
                                            action={() =>
                                                api?.facilityControllerDeleteNurseVerification(
                                                    {
                                                        profileId: row.id,
                                                        facilityId:
                                                            selection.id,
                                                    }
                                                )
                                            }
                                        >
                                            Unverify
                                        </ActionButton>
                                    ) : (
                                        <ActionButton
                                            size="small"
                                            successMessage="Nurse verified"
                                            action={() =>
                                                api?.facilityControllerVerifyNurse(
                                                    {
                                                        profileId: row.id,
                                                        facilityId:
                                                            selection.id,
                                                    }
                                                )
                                            }
                                        >
                                            Verify
                                        </ActionButton>
                                    )}

                                    <ActionButton
                                        size="small"
                                        successMessage="Nurse blocked"
                                        action={() =>
                                            api?.facilityControllerBlockNurse({
                                                profileId: row.id,
                                                facilityId: selection.id,
                                            })
                                        }
                                    >
                                        Block
                                    </ActionButton>
                                </Flex>
                            ),
                        },
                    ]}
                    loading={isLoading}
                />
            </div>
            <div style={{ marginTop: 20 }}>
                <Title>Blocked Nurses</Title>
                <Flex gap="small" style={{ marginBottom: 12 }} align="center">
                    <ProfileAutoComplete
                        key={`block-${blockKey}`}
                        autoFocus={false}
                        placeholder="Search nurse to block"
                        role="Nurse"
                        onSelect={(profile) => setNurseToBlock(profile)}
                    />
                    <Select
                        allowClear
                        placeholder="Reason (optional)"
                        style={{ width: 220 }}
                        value={blockReason}
                        options={blockReasonOptions}
                        onChange={(value) =>
                            setBlockReason(
                                value as FacilityNurseBlockReason | undefined
                            )
                        }
                    />
                    <ActionButton
                        size="small"
                        type="primary"
                        disabled={!nurseToBlock}
                        successMessage="Nurse blocked"
                        action={async () => {
                            if (!api || !nurseToBlock) {
                                return;
                            }
                            await api.facilityControllerBlockNurse({
                                profileId: nurseToBlock.id,
                                facilityId: selection.id,
                                reason: blockReason,
                            });
                            setNurseToBlock(null);
                            setBlockReason(undefined);
                            setBlockKey((k) => k + 1);
                        }}
                    >
                        Block nurse
                    </ActionButton>
                </Flex>
                <Table
                    rowKey="id"
                    bordered
                    size="small"
                    pagination={false}
                    dataSource={nurses.filter((nurse) => nurse.blockedAt)}
                    columns={[
                        {
                            title: 'First Name',
                            dataIndex: 'firstName',
                        },
                        {
                            title: 'Last Name',
                            dataIndex: 'lastName',
                        },
                        {
                            title: 'Reason',
                            render: (_, row) =>
                                row.reason
                                    ? BLOCK_REASON_LABELS[row.reason]
                                    : '—',
                        },
                        {
                            title: 'Blocked By',
                            render: (_, row) => (
                                <>
                                    {row.blockedBy} on{' '}
                                    {row.blockedAt?.toLocaleDateString()}
                                </>
                            ),
                        },
                        {
                            title: 'Actions',
                            render: (_, row) => (
                                <>
                                    <ActionButton
                                        size="small"
                                        successMessage="Nurse unblocked"
                                        action={() =>
                                            api?.facilityControllerUnblockNurse(
                                                {
                                                    profileId: row.id,
                                                    facilityId: selection.id,
                                                }
                                            )
                                        }
                                    >
                                        Unblock
                                    </ActionButton>
                                </>
                            ),
                        },
                    ]}
                    loading={isLoading}
                />
            </div>
        </FetchRecordProvider>
    );
}
